import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useI18n } from "../i18n";
import { useCart } from "../cart";
import { BRAND } from "../brand";

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const links = [
    { to: "/shop", label: t.nav.shop, id: "nav-shop" },
    { to: "/collections", label: t.nav.collections, id: "nav-collections" },
    { to: "/about", label: t.nav.about, id: "nav-about" },
    { to: "/contact", label: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen ? "bg-black border-b border-white/10" : "bg-black/30 backdrop-blur-xl"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <span className="font-gothic text-2xl md:text-3xl text-blood leading-none">Beaudoin</span>
        </Link>

        <nav className="hidden md:flex items-center gap-12">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `link-hover text-xs font-bold tracking-[0.2em] uppercase ${isActive ? "text-white" : "text-white/70 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-6">
          <div className="hidden sm:flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase">
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`transition-colors ${lang === "en" ? "text-white" : "text-white/40 hover:text-white/70"}`}
            >EN</button>
            <span className="text-white/20">/</span>
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`transition-colors ${lang === "fr" ? "text-white" : "text-white/40 hover:text-white/70"}`}
            >FR</button>
          </div>

          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 text-white hover:text-white/70 transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-3 bg-white text-black text-[10px] font-black w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-white"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-black border-t border-white/10 px-6 py-8 space-y-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className="block text-2xl font-black tracking-tight uppercase"
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex gap-4 pt-4 border-t border-white/10 text-xs font-bold tracking-[0.2em] uppercase">
            <button onClick={() => setLang("en")} className={lang === "en" ? "text-white" : "text-white/40"}>EN</button>
            <button onClick={() => setLang("fr")} className={lang === "fr" ? "text-white" : "text-white/40"}>FR</button>
          </div>
        </div>
      )}
    </header>
  );
}
